import React, { useState, useCallback } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import Avatar from "react-avatar"
import ReactTooltip from "react-tooltip"

import { CardHeading } from "components"

import PassengersModal from "components/Common/Modals/Passengers"

const PASSENGERS = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      passengers {
        id
        relationship
        firstName
        lastName
        email
        phone
        gender
        birthdate

        street
        city
        state
        region
        zip
        country

        # ADDITIONAL
        passportNumber
        passportExparationDate

        weight
        height
        size

        emergencyContactPerson
        specialRequest

        roomateRequest
        smoker
        newsletter

        bookingId
        tripId
        userId

        user {
          id
          displayName
          email
        }
      }

      settings {
        passengerFields
      }
    }
  }
`

const CREATE_TRIP_PASSENGERS = gql`
  mutation CreateTripPassengers($input: CreateTripPassengersInput!) {
    createTripPassengers(input: $input) {
      id
    }
  }
`

const UPDATE_TRIP_PASSENGERS = gql`
  mutation UpdateTripPassengers($input: UpdateTripPassengersInput!) {
    updateTripPassengers(input: $input) {
      id
      relationship
      firstName
      lastName
      email
      phone
      gender
      birthdate

      street
      city
      state
      region
      zip
      country

      # ADDITIONAL
      passportNumber
      passportExparationDate

      weight
      height
      size

      emergencyContactPerson
      specialRequest

      roomateRequest
      smoker
      newsletter

      bookingId
      tripId
      userId

      user {
        id
        displayName
        email
      }
    }
  }
`

const DELETE_TRIP_PASSENGER = gql`
  mutation DeleteTripPassenger($input: DeleteTripPassengerInput!) {
    deleteTripPassenger(input: $input) {
      id
    }
  }
`

const TripPassengersContainer = ({ tripId }) => {
  const [createTripPassengers] = useMutation(CREATE_TRIP_PASSENGERS)
  const [updateTripPassengers] = useMutation(UPDATE_TRIP_PASSENGERS)
  const [deleteTripPassenger] = useMutation(DELETE_TRIP_PASSENGER)

  const handleCreate = async (passengers) => {
    try {
      const input = { tripId, passengers }

      await createTripPassengers({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: PASSENGERS,
            variables: { id: tripId },
          })

          const existingPassengers = existing?.trip?.passengers

          cache.writeQuery({
            query: PASSENGERS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                passengers: [
                  ...(existingPassengers ? existingPassengers : []),
                  ...data?.createTripPassengers,
                ],
              },
            },
          })
        },
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const handleUpdate = async (passengers) => {
    try {
      const input = { tripId, passengers }

      await updateTripPassengers({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: PASSENGERS,
            variables: { id: tripId },
          })

          const passengers = existing?.trip?.passengers
          const updatePassengers = data?.updateTripPassengers

          cache.writeQuery({
            query: PASSENGERS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                passengers: [
                  ...passengers.map((item) => {
                    const isUpdate = updatePassengers.find(
                      (_) => _.id === item.id,
                    )
                    return isUpdate ? isUpdate : item
                  }),
                ],
              },
            },
          })
        },
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const handleDelete = async (passenger) => {
    if (window.confirm("Are you sure you want to delete this passenger?")) {
      try {
        const input = { tripId, id: passenger.id }

        await deleteTripPassenger({
          variables: { input },
          update(cache, { data }) {
            const existing = cache.readQuery({
              query: PASSENGERS,
              variables: { id: tripId },
            })

            const passengers = existing?.trip?.passengers
            const deleted = data?.deleteTripPassenger

            cache.writeQuery({
              query: PASSENGERS,
              variables: { id: tripId },
              data: {
                trip: {
                  ...existing?.trip,
                  passengers: passengers.filter((_) => _.id === deleted.id),
                },
              },
            })
          },
        })
      } catch (error) {
        console.error(error)
        throw error
      }
    }
  }

  const { data, loading, error } = useQuery(PASSENGERS, {
    variables: { id: tripId },
  })

  const passengers = data?.trip?.passengers

  const [selectedPassenger, setSelectedPassenger] = useState(null)

  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setIsEditing(false)
  }, [setIsEditing])

  const isEmpty = !data || passengers?.length === 0

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {isEmpty ? (
        <>
          <CardHeading title="Passengers" />
          <PassengersEmpty isLoading={loading} onClick={openModal} />
        </>
      ) : (
        <>
          <CardHeading
            title="Passengers"
            ctaTitle="Add Passenger"
            onClick={openModal}
          />

          <PassengersTable
            data={passengers}
            onSelect={(id) => {
              console.info("ON SELECT", id)
            }}
            onEdit={(passenger) => {
              setSelectedPassenger(passenger)
              openModal()
            }}
            onDelete={handleDelete}
          />
        </>
      )}

      <PassengersModal
        max={Infinity}
        //
        isOpen={isEditing}
        onClose={closeModal}
        //
        tripId={tripId}
        //
        fields={{ ...data?.trip?.settings?.passengerFields, userId: true }}
        data={selectedPassenger ? [selectedPassenger] : []}
        //
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </div>
  )
}

export default TripPassengersContainer

//
const PassengersEmpty = ({ isLoading, onClick }) => {
  return (
    <div className="py-16 flex flex-col justify-center items-center">
      {isLoading ? (
        <div>...Loading...</div>
      ) : (
        <>
          {" "}
          <p className="text-center">No Passengers</p>
          <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add passenger
          </button>
        </>
      )}
    </div>
  )
}

const PassengersTable = ({ data, onSelect, onEdit, onDelete }) => {
  const header = ["Name", "Lead passenger", "Passport", "Special Request", ""]

  return (
    <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
      <table className="min-w-full table-auto relative">
        <thead>
          <tr>
            {header.map((item, index) => (
              <th
                key={index}
                className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                style={{ position: "sticky", top: "0" }}
              >
                {item}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {data?.map((passenger, index) => (
            <tr key={index} onClick={() => onSelect(passenger.id)}>
              <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <Avatar
                      name={`${passenger?.firstName} ${passenger?.lastName}`}
                      // src={user.photoURL}
                      size="40"
                      round={true}
                    />
                  </div>
                  <div className="ml-4">
                    <div className="text-sm leading-5 font-medium text-gray-900">
                      {passenger?.firstName} {passenger?.lastName}
                    </div>
                    <div className="text-sm leading-5 text-gray-500">
                      {passenger?.email}
                    </div>
                  </div>
                </div>
              </td>
              <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                <div className="text-sm leading-5 font-medium text-gray-900">
                  {passenger?.user?.displayName}
                </div>
                <div className="text-sm leading-5 text-gray-500">
                  {passenger?.user?.email}
                </div>
              </td>
              <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500"></td>
              <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium">
                <div className="text-sm leading-5 text-gray-900"></div>
                <div className="bg-gray-100 w-full h-4 flex"></div>
              </td>
              <td className="w-1/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium">
                <div data-for="action" data-tip="hello world">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                    />
                  </svg>
                </div>

                <ReactTooltip
                  event="click"
                  // eventOff="click"
                  globalEventOff="click"
                  isCapture={true}
                  id="action"
                  place="bottom"
                  // type="dark"
                  effect="solid"
                  clickable={true}
                  getContent={() => {
                    return (
                      <ul>
                        <li>
                          <button
                            type="button"
                            className="text-blue-500"
                            onClick={() => onEdit(passenger)}
                          >
                            Edit
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="text-red-500"
                            onClick={() => onDelete(passenger)}
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    )
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
